































































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { ContentFile } from "@scrile/api-provider/dist/api/ContentFilesProvider";
import useAuthorisedOnly from "@/hooks/useAuthorisedOnly";
import emitter from "@/lib/emitter";
import { isVideoFile } from "@/lib/fileHelpers";
import { EVENTS } from "@/const";
import BaseDuration from "@/components/BaseDuration";
import BaseIcon from "@/components/BaseIcon";
import { ProcessedContentFiles } from "@/components/ContentFeedItem";
import FullScreen from "@/components/FullScreen";
import GalleryTotalItems from "@/components/GalleryTotalItems";
import ContentFeedGalleryItem from "../ContentFeedGalleryItem";

interface FileListInfo {
  accessed: boolean;
  file: ContentFile | null;
  id: string;
}

interface AllFiles {
  audio?: ProcessedContentFiles;
  media: ProcessedContentFiles;
  documents: ProcessedContentFiles;
}

export default defineComponent({
  name: "ContentFeedGallery",
  components: { BaseDuration, FullScreen, ContentFeedGalleryItem, BaseIcon, GalleryTotalItems },
  props: {
    files: { type: Object as () => AllFiles, required: true },
    feedId: { type: String, required: true },
    isSafeForWork: { type: Boolean, required: true },
    showNsfwContent: { type: Boolean, default: false },
  },
  setup(props) {
    const currentItemSetter = ref<(count: number) => void>();
    const isActive = ref<boolean>(false);
    const currentItemIndex = ref(0);
    const fileClicked = ref<boolean>(false);

    const accessedMediaFiles = computed(() => props.files.media.accessed);
    const lockedMediaFiles = computed(() => props.files.media.locked);
    const lockedAudioFiles = computed(() => props.files.audio?.locked ?? []);
    const lockedDocumentFiles = computed(() => props.files.documents.locked);
    const allFiles = computed(() => {
      return accessedMediaFiles.value.concat(lockedMediaFiles.value, lockedDocumentFiles.value, lockedAudioFiles.value);
    });

    const allLockedFiles = computed(() => {
      return lockedMediaFiles.value.concat(lockedDocumentFiles.value, lockedAudioFiles.value);
    });
    const listLength = computed(() => {
      return (
        accessedMediaFiles.value.length +
        lockedMediaFiles.value.length +
        lockedDocumentFiles.value.length +
        lockedAudioFiles.value.length
      );
    });
    const lockFullScreen = ref<boolean>(false);

    const renderFileList = computed<FileListInfo[]>(() => {
      const lockedFiles = allLockedFiles.value.map(file => ({ accessed: false, file, id: file.id } as FileListInfo));
      return accessedMediaFiles.value
        .map(file => ({ accessed: true, file, id: file.id } as FileListInfo))
        .concat(lockedFiles);
    });

    const galleryItemStyle = computed(() => {
      const file = accessedMediaFiles.value.length ? accessedMediaFiles.value[0] : lockedMediaFiles.value[0];
      if (file && file.width > 0 && file.height > 0) {
        return {
          "padding-bottom": `${Math.min(Math.ceil((file.height / file.width) * 100), 150)}%`,
        };
      }
      return { "padding-bottom": "57%" };
    });

    const scalable = computed(
      () =>
        accessedMediaFiles.value[currentItemIndex.value] &&
        !isVideoFile(accessedMediaFiles.value[currentItemIndex.value])
    );

    const closeFullScreen = () => {
      fileClicked.value = false;
    };

    const onChangeIndex = (newIndex: number) => {
      currentItemIndex.value = newIndex;
    };

    const onItemClick = useAuthorisedOnly((currentItem: number, isFullScreen: boolean, cb: () => void) => {
      if (!accessedMediaFiles.value[currentItem]) return;
      if (!props.showNsfwContent && !props.isSafeForWork) {
        emitter.emit(EVENTS.ENABLE_NSFW, () => {
          cb();
        });
        return;
      }
      if (!isFullScreen) {
        cb();
        return;
      }
      fileClicked.value = true;
    });

    watch(allFiles, (files: ContentFile[], old: ContentFile[]) => {
      if (isEqual(files, old)) return;
      currentItemSetter.value && currentItemSetter.value(0);
    });

    return {
      accessedMediaFiles,
      lockedMediaFiles,
      galleryItemStyle,
      isActive,
      fileClicked,
      closeFullScreen,
      listLength,
      currentItemSetter,
      scalable,
      onChangeIndex,
      renderFileList,
      lockFullScreen,
      allLockedFiles,
      allFiles,
      isVideoFile,
      onItemClick,
    };
  },
});
